/** A selectable asset tile with one state signal and quiet source metadata. */

import { Check, FileText, ImageIcon, Layers, PenTool } from "lucide-react"
import { COPY } from "@/lib/copyTokens"
import { cn } from "@/lib/utils"
import type { ClientImageAssetListItem } from "@/services/clientImageAssetsService"
import { AssetStateChip } from "./AssetStateChip"

export interface AssetCardProps {
  item: ClientImageAssetListItem
  onSelect: (assetId: string) => void
  className?: string
}

const VECTOR_EXTENSIONS = new Set(["ai", "eps", "svg"])
const DOCUMENT_EXTENSIONS = new Set(["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx"])
const RASTER_SOURCE_EXTENSIONS = new Set(["psd", "psb", "tif", "tiff", "dng", "raw"])

function isAiLabelSource(labelSource: string) {
  const normalized = labelSource.toLowerCase()
  return normalized.includes("codex") || normalized.includes("fireworks")
}

function FileTile({ extension }: { extension?: string | null }) {
  const normalizedExtension = extension?.toLowerCase() ?? ""
  const label = extension?.toUpperCase() || "FILE"
  const Icon = VECTOR_EXTENSIONS.has(normalizedExtension)
    ? PenTool
    : RASTER_SOURCE_EXTENSIONS.has(normalizedExtension)
      ? Layers
      : DOCUMENT_EXTENSIONS.has(normalizedExtension)
        ? FileText
        : ImageIcon
  const family = VECTOR_EXTENSIONS.has(normalizedExtension)
    ? "vector"
    : RASTER_SOURCE_EXTENSIONS.has(normalizedExtension)
      ? "raster-source"
      : DOCUMENT_EXTENSIONS.has(normalizedExtension)
        ? "document"
        : "image"

  return (
    <div
      data-testid={`asset-no-preview-${family}`}
      className="flex min-h-[110px] flex-col items-center justify-center gap-1.5 bg-muted text-muted-foreground"
    >
      <Icon aria-hidden="true" className="h-7 w-7" />
      <span className="text-xs font-semibold tracking-wide">{label}</span>
      <span className="text-[11px] text-muted-foreground">{COPY.mediaAssets.noPreview}</span>
    </div>
  )
}

function AssetMetadata({ item }: Pick<AssetCardProps, "item">) {
  const humanApproved = Boolean(item.reviewed_at) && item.label_status === "approved"
  const aiLabeled = !humanApproved && isAiLabelSource(item.label_source)

  if (!item.category && !humanApproved && !aiLabeled) return null

  return (
    <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
      {item.category ? <span data-testid="asset-category" className="text-muted-foreground">{item.category}</span> : null}
      {item.category && (humanApproved || aiLabeled) ? <span aria-hidden="true">·</span> : null}
      {humanApproved ? (
        <span data-testid="asset-human-approved" className="inline-flex items-center gap-1 font-semibold text-teal-dark">
          <Check data-testid="asset-human-approved-check" aria-hidden="true" className="h-3.5 w-3.5 text-teal" />
          {COPY.mediaAssets.humanApproved}
        </span>
      ) : null}
      {aiLabeled ? <span data-testid="asset-ai-labeled" className="text-muted-foreground">{COPY.mediaAssets.aiLabeled}</span> : null}
    </div>
  )
}

export function AssetCard({ item, onSelect, className }: AssetCardProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item.id)}
      title={item.filename}
      className={cn(
        "group flex flex-col overflow-hidden rounded-2xl border border-border bg-card text-left transition-colors hover:border-coral/40 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/50",
        className,
      )}
    >
      <div className="aspect-[4/3] min-h-[110px] w-full overflow-hidden bg-muted">
        {item.url ? (
          <img
            src={item.url}
            alt={item.alt_text || item.filename}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
          />
        ) : (
          <FileTile extension={item.extension} />
        )}
      </div>
      <div className="space-y-2 p-3">
        <p className="truncate text-[13px] font-medium text-foreground" title={item.filename}>
          {item.filename}
        </p>
        <div className="flex items-center justify-between gap-2">
          <AssetMetadata item={item} />
          <span data-testid="asset-state" className="shrink-0">
            <AssetStateChip state={item.state} className="text-[11px]" />
          </span>
        </div>
        <span className="block text-[11px] text-ink-3 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          {COPY.mediaAssets.openDetails}
        </span>
      </div>
    </button>
  )
}

export default AssetCard
